import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { AutomationStatus } from '@/api/automation'
import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClient } from '@/api/client'
import { drillDownHref, type DrillDown } from '@/lib/drilldown'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

/** Automation has no global-filter dependency: a catalogue and a queue, not a metrics view. */
export function useAutomationQuery<T>(name: string, fetch: (client: ApiClient) => Promise<T>) {
  const client = useApiClient()
  return useQuery({ queryKey: ['automation', name, client.kind], queryFn: () => fetch(client) })
}

/** Builds a drill-down link that carries the current filters, like every other feature's. */
export function useDrillDown() {
  const { filters } = useGlobalFilters()
  return (target: DrillDown) => drillDownHref(target, filters)
}

function useInvalidateAutomation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['automation'] })
}

export function useSetWorkflowStatus() {
  const client = useApiClient()
  const invalidate = useInvalidateAutomation()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AutomationStatus }) => client.setWorkflowStatus(id, status),
    onSuccess: () => invalidate(),
  })
}

export function useCancelScheduledAction() {
  const client = useApiClient()
  const invalidate = useInvalidateAutomation()
  return useMutation({
    mutationFn: (id: string) => client.cancelScheduledAction(id),
    onSuccess: () => invalidate(),
  })
}
